import {
  FormatDistanceToken,
  FormatDurationOptions,
  addDays,
  format as dateFnsFormat,
  formatDistance,
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
  isSameDay,
  isValid,
  parse,
  startOfWeek,
} from "date-fns";

// Dates at or before this are considered invalid (e.g. new Date(null) = epoch)
const MIN_VALID_DATE = new Date("1971-01-01T00:00:00.000Z");

export const parseDate = (
  value: string | Date | number | null | undefined,
  dateFormat: string = "yyyy-MM-dd HH:mm",
): Date | null => {
  if (value === null || value === undefined) return null;
  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    date = parse(value, dateFormat, new Date());
  } else if (typeof value === "number") {
    date = new Date(value);
  }

  if (!date || !isValid(date) || date <= MIN_VALID_DATE) return null;
  return date;
};

export const formatDate = (
  value: string | Date | number | null | undefined,
  dateFormat: string = "dd/MM/yyyy HH:mm",
): string => {
  const date = parseDate(value);
  if (!date) return "";
  return dateFnsFormat(date, dateFormat);
};

export const formatDateRelative = (
  value: string | Date | number | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  const date = parseDate(value);
  if (!date) return "";
  return formatDistanceToNow(date, options);
};

export const formatDateDistance = (
  dateLeft: string | Date | number | null | undefined,
  dateRight: string | Date | number | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  const left = parseDate(dateLeft);
  const right = parseDate(dateRight);
  if (!left || !right) return "";
  return formatDistance(left, right, options);
};

export const datePlusDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const dateStart = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const dateEnd = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const formatSecondsToDuration = (
  seconds: number,
  options?: Omit<FormatDurationOptions, "format"> & {
    format?: {
      years?: string;
      months?: string;
      weeks?: string;
      days?: string;
      hours?: string;
      minutes?: string;
      seconds?: string;
    };
  },
): string => {
  const { format, ...restOptions } = options || {};
  if (format) {
    const { years, months, weeks, days, hours, minutes, seconds } = format;

    const formatDistanceLocale: Partial<Record<FormatDistanceToken, string>> = {
      xYears: years && `{{count}}${years}`,
      xMonths: months && `{{count}}${months}`,
      xWeeks: weeks && `{{count}}${weeks}`,
      xDays: days && `{{count}}${days}`,
      xHours: hours && `{{count}}${hours}`,
      xMinutes: minutes && `{{count}}${minutes}`,
      xSeconds: seconds && `{{count}}${seconds}`,
    };
    const shortEnLocale = {
      formatDistance: (token: FormatDistanceToken, count: number) => {
        let val = (count || "00").toString();
        if (val.length < 2 && restOptions.zero) {
          val = "0" + val;
        }
        return formatDistanceLocale[token]?.replace("{{count}}", val) || "";
      },
    };
    restOptions.locale = shortEnLocale;
  }

  return formatDuration(
    intervalToDuration({ start: 0, end: seconds * 1000 }),
    restOptions,
  );
};

export const getDayOfWeekAsDate = (dayOfWeek: number): Date => {
  const startOfWeekDay = startOfWeek(new Date());
  const daysToAdd = [0, 1, 2, 3, 4, 5, 6].indexOf(dayOfWeek);
  let currentDate = addDays(startOfWeekDay, daysToAdd);

  // Ensure we're not returning a past date
  if (isSameDay(currentDate, startOfWeekDay)) {
    currentDate = addDays(currentDate, 7);
  }

  return currentDate;
};
